/**
 * UserManualPrint — A4-friendly, chromeless render of the user manual.
 *
 *  - No app header / sidebar (rendered outside AppLayout).
 *  - Brand logo + client name in the print header (KCS branding).
 *  - Page breaks between H2 sections.
 *  - Auto-triggers window.print() once the markdown + images have loaded.
 *  - The user chooses "Save as PDF" from the browser print dialog.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/auth";

const MANUAL_URL = "/manual/user-manual.md";

function useBrandClient() {
  const { assignments } = useAuth();
  const [brand, setBrand] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const clientId = assignments?.find((a) => a.client_id)?.client_id;
      if (!clientId) return;
      const { data } = await supabase
        .from("lbs_clients")
        .select("id,name,brand_logo_url,brand_primary_color")
        .eq("id", clientId)
        .maybeSingle();
      if (!cancelled) setBrand(data || null);
    })();
    return () => { cancelled = true; };
  }, [assignments]);
  return brand;
}

const printComponents = {
  img: ({ src = "", alt = "", ...rest }) => {
    const rewritten = src.startsWith("img/user-manual/")
      ? "/manual/" + src.replace(/^img\/user-manual\//, "")
      : src;
    return (
      <img
        src={rewritten}
        alt={alt}
        className="my-3 rounded-sm border border-slate-300 max-w-full"
        {...rest}
      />
    );
  },
  table: ({ children }) => (
    <table className="w-full border-collapse text-[11pt] my-3 border border-slate-300">
      {children}
    </table>
  ),
  th: ({ children }) => (
    <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left font-semibold">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-slate-300 px-2 py-1 align-top">
      {children}
    </td>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code className="bg-slate-100 px-1 rounded text-[0.9em] font-mono">{children}</code>
    ) : (
      <code>{children}</code>
    ),
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-4 border-slate-400 bg-slate-50 px-3 py-1 text-slate-700">
      {children}
    </blockquote>
  ),
};

export default function UserManualPrint() {
  const [md, setMd] = useState("");
  const brand = useBrandClient();
  const printedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetch(MANUAL_URL, { cache: "no-store" });
      const text = await res.text();
      if (!cancelled) setMd(text);
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-open print dialog once content is in the DOM and images have started to render.
  useEffect(() => {
    if (!md || printedRef.current) return;
    printedRef.current = true;
    const t = setTimeout(() => {
      try { window.print(); } catch { /* user can Cmd/Ctrl-P manually */ }
    }, 900);
    return () => clearTimeout(t);
  }, [md]);

  const today = useMemo(
    () =>
      new Date().toLocaleDateString("en-AU", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="user-manual-print-page">
      {/* Print header — repeats top of each printed page via @page CSS below */}
      <header className="print-header flex items-center justify-between border-b border-slate-300 px-8 py-4">
        <div className="flex items-center gap-3">
          {brand?.brand_logo_url ? (
            <img
              src={brand.brand_logo_url}
              alt={`${brand.name} logo`}
              className="h-10 w-10 object-contain"
              data-testid="print-brand-logo"
            />
          ) : null}
          <div>
            <div className="text-sm font-semibold">
              {brand?.name ? `${brand.name} — ` : ""}User manual
            </div>
            <div className="text-[11px] text-slate-500">timeslot.au · generated {today}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 no-print"
          data-testid="print-manual-btn"
        >
          Print / Save as PDF
        </button>
      </header>

      <article className="print-manual px-8 py-6 max-w-[820px] mx-auto" data-testid="print-manual-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSlug]}
          components={printComponents}
        >
          {md}
        </ReactMarkdown>
      </article>

      <style>{`
        @page { size: A4; margin: 18mm 14mm; }
        .print-manual { color: #0f172a; line-height: 1.55; font-size: 11pt; font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
        .print-manual h1 { font-size: 22pt; font-weight: 700; margin: 0 0 8pt; }
        .print-manual h2 { font-size: 16pt; font-weight: 700; margin: 18pt 0 6pt; padding-bottom: 3pt; border-bottom: 1px solid #cbd5e1; page-break-before: always; }
        .print-manual h2:first-of-type { page-break-before: auto; }
        .print-manual h3 { font-size: 13pt; font-weight: 600; margin: 12pt 0 4pt; }
        .print-manual h4 { font-size: 11pt; font-weight: 600; margin: 10pt 0 3pt; }
        .print-manual p, .print-manual li { orphans: 3; widows: 3; }
        .print-manual ul, .print-manual ol { margin: 4pt 0 6pt 18pt; }
        .print-manual table { page-break-inside: avoid; }
        .print-manual img { page-break-inside: avoid; }
        .print-manual hr { margin: 12pt 0; border: 0; border-top: 1px solid #cbd5e1; }
        .print-manual a { color: #1d4ed8; text-decoration: none; }

        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          .print-header { border-bottom: 1px solid #94a3b8; padding: 8pt 0; }
        }
      `}</style>
    </div>
  );
}
